package com.qrkontaq.ui.tenant

import com.qrkontaq.core.config.AppConfig
import com.qrkontaq.utils.Storage
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.w3c.fetch.RequestCredentials
import org.w3c.fetch.RequestInit
import kotlin.js.json

/** fetch credentials mode that sends/accepts the API's httpOnly session cookie. */
private val INCLUDE_CREDENTIALS = "include".unsafeCast<RequestCredentials>()

/**
 * Diner-side session for the public menu, independent from the admin Session:
 * a guest logs in only at the moment of an action (like), per product decision.
 */
object GuestSession {
    private const val KEY = "guest_access_token"
    private const val EMAIL_KEY = "guest_email"
    private const val OAUTH_PROVIDER_KEY = "guest_oauth_provider"

    /** Domain of the silently provisioned anonymous accounts (never OAuth). */
    private const val ANON_EMAIL_DOMAIN = "@guest.qrkontaq.local"

    var token: String?
        get() = Storage.get(KEY)?.takeIf { it.isNotBlank() }
        set(value) {
            Storage.save(KEY, value.orEmpty())
        }

    var email: String?
        get() = Storage.get(EMAIL_KEY)?.takeIf { it.isNotBlank() }
        set(value) {
            Storage.save(EMAIL_KEY, value.orEmpty())
        }

    /** "GOOGLE"/"APPLE" when the current token came from an explicit OAuth sign-in. */
    var oauthProvider: String?
        get() = Storage.get(OAUTH_PROVIDER_KEY)?.takeIf { it.isNotBlank() }
        set(value) {
            Storage.save(OAUTH_PROVIDER_KEY, value.orEmpty())
        }

    val isLoggedIn: Boolean get() = token != null

    /** Email of the silently provisioned anon account, if this browser has one. */
    val anonEmail: String?
        get() = email?.takeIf { it.endsWith(ANON_EMAIL_DOMAIN) }

    /**
     * True only when the guest EXPLICITLY signed in via Google or Apple.
     * Anonymous `anon…@guest.qrkontaq.local` sessions (and any session predating
     * the OAuth flag) are never OAuth, even if a stale flag is around.
     */
    val isOAuth: Boolean
        get() = token != null &&
            oauthProvider != null &&
            email?.endsWith(ANON_EMAIL_DOMAIN) != true

    fun logout() {
        token = null
        email = null
        oauthProvider = null
    }

    // --- One-vote-per-browser memory (used while OAuth is temporarily disabled) ---

    private const val RATED_PREFIX = "guest_rated_"

    /** The score this browser already gave to a target, if any. */
    fun ratedScore(targetId: String): Int? =
        Storage.get("$RATED_PREFIX$targetId")?.toIntOrNull()

    fun setRated(targetId: String, score: Int) {
        Storage.save("$RATED_PREFIX$targetId", score.toString())
    }

    private const val LIKED_ITEMS_KEY = "guest_liked_items"

    /**
     * Item ids this browser already liked — the heart survives reloads and token
     * expiry without a network call, and marks that this client already voted.
     */
    fun likedItemsLocally(): Set<String> =
        Storage.get(LIKED_ITEMS_KEY)
            ?.split(',')
            ?.filter { it.isNotBlank() }
            ?.toSet()
            .orEmpty()

    fun setLikedLocally(
        itemId: String,
        liked: Boolean,
    ) {
        val ids = likedItemsLocally().toMutableSet()
        if (liked) ids.add(itemId) else ids.remove(itemId)
        Storage.save(LIKED_ITEMS_KEY, ids.joinToString(","))
    }
}

/** Thin fetch-based client for guest actions (no shared ktor/auth state with the admin). */
object GuestApi {

    /**
     * Ensures there is a guest token: silently provisions an anonymous per-browser
     * account so likes and ratings work without a sign-in form. A previously
     * provisioned account is re-logged into (never re-registered), so a browser
     * votes as the SAME user every time and the server dedups its votes.
     */
    suspend fun ensureSession(): Boolean {
        if (GuestSession.token != null) return true
        if (tryAnonRelogin()) return true
        val id = kotlin.random.Random.nextLong(100_000_000_000L, 999_999_999_999L)
        return register("anon$id@guest.qrkontaq.local", "Anon$id!aA1")
    }

    /**
     * Recovers the SAME anonymous account after the 15-minute access token expires:
     * the password is derived from the stored anon email, so likes/votes keep
     * belonging to one user instead of a fresh account per session.
     */
    private suspend fun tryAnonRelogin(): Boolean {
        val email = GuestSession.anonEmail ?: return false
        val id = email.substringBefore("@").removePrefix("anon")
        if (id.isBlank()) return false
        return runCatching { login(email, "Anon$id!aA1") }.getOrDefault(false)
    }

    suspend fun callWaiter(
        slug: String,
        tableLabel: String,
        type: String,
    ): Boolean {
        // When the guest is signed in, the Bearer token lets the backend attach their
        // identity + loyalty to the call so the waiter sees who is asking.
        val headers = json("Content-Type" to "application/json")
        GuestSession.token?.let { headers["Authorization"] = "Bearer $it" }
        val response =
            window.fetch(
                AppConfig.getApiUrl("v1/public/restaurants/$slug/waiter-calls"),
                RequestInit(
                    method = "POST",
                    headers = headers,
                    body = JSON.stringify(json("tableLabel" to tableLabel, "type" to type)),
                ),
            ).await()
        return response.ok
    }

    /**
     * Records a QR scan of the venue/table. When the guest is signed in the Bearer token
     * is attached, so the scan carries their identity — this is what lets the waiter see
     * the guest (name + loyalty) ahead of the bill. Anonymous scans still count for stats.
     */
    suspend fun recordScan(
        slug: String,
        tableLabel: String?,
        categorySlug: String?,
    ): Boolean {
        val headers = json("Content-Type" to "application/json")
        GuestSession.token?.let { headers["Authorization"] = "Bearer $it" }
        val body = json("source" to "QR_WEB")
        if (tableLabel != null) body["tableLabel"] = tableLabel
        if (categorySlug != null) body["categorySlug"] = categorySlug
        val response =
            runCatching {
                window.fetch(
                    AppConfig.getApiUrl("v1/public/restaurants/$slug/scan"),
                    RequestInit(
                        method = "POST",
                        headers = headers,
                        body = JSON.stringify(body),
                    ),
                ).await()
            }.getOrNull()
        return response?.ok == true
    }

    /** Logs in an existing diner; returns true on success. */
    suspend fun login(
        email: String,
        password: String,
    ): Boolean =
        authenticate("v1/auth/login", json("email" to email, "password" to password))
            .also {
                if (it) {
                    GuestSession.email = email
                    GuestSession.oauthProvider = null
                }
            }

    /** Registers a new diner (username derived from email); returns true on success. */
    suspend fun register(
        email: String,
        password: String,
    ): Boolean {
        val username =
            email.substringBefore("@")
                .replace(Regex("[^a-zA-Z0-9_]"), "_")
                .take(20)
                .ifBlank { "guest" } + "_" + (10000..99999).random()
        return authenticate(
            "v1/auth/register",
            json("email" to email, "username" to username, "password" to password),
        ).also {
            if (it) {
                GuestSession.email = email
                GuestSession.oauthProvider = null
            }
        }
    }

    // --- OAuth sign-in (the only way a guest account can join the loyalty program) ---

    /**
     * Logs in with the raw Google Identity Services credential (a signed JWT); the backend
     * verifies it and derives the identity itself. Marks the session as OAuth on success.
     * [email] is only kept locally for display; it is never sent to the server.
     */
    suspend fun loginWithGoogle(
        idToken: String,
        email: String? = null,
        fullName: String? = null,
        avatarUrl: String? = null,
    ): Boolean = oauthLogin("google", "GOOGLE", idToken, email, fullName, avatarUrl)

    /**
     * Logs in with the raw Sign in with Apple id_token; the backend verifies it and derives
     * the identity itself. [fullName] is a hint (Apple exposes it only on first consent).
     */
    suspend fun loginWithApple(
        idToken: String,
        email: String? = null,
        fullName: String? = null,
    ): Boolean = oauthLogin("apple", "APPLE", idToken, email, fullName, avatarUrl = null)

    private suspend fun oauthLogin(
        path: String,
        provider: String,
        idToken: String,
        email: String?,
        fullName: String?,
        avatarUrl: String?,
    ): Boolean {
        val body = json("idToken" to idToken)
        if (!fullName.isNullOrBlank()) body["fullName"] = fullName
        if (!avatarUrl.isNullOrBlank()) body["avatarUrl"] = avatarUrl
        return authenticate("v1/auth/oauth/$path", body).also {
            if (it) {
                if (!email.isNullOrBlank()) GuestSession.email = email
                GuestSession.oauthProvider = provider
            }
        }
    }

    private suspend fun authenticate(
        path: String,
        body: dynamic,
    ): Boolean {
        val response =
            window.fetch(
                AppConfig.getApiUrl(path),
                RequestInit(
                    method = "POST",
                    headers = json("Content-Type" to "application/json"),
                    body = JSON.stringify(body),
                    // Lets the backend set/refresh the httpOnly SSO cookie.
                    credentials = INCLUDE_CREDENTIALS,
                ),
            ).await()
        if (!response.ok) return false
        val text = response.text().await()
        val token =
            runCatching { JSON.parse<dynamic>(text).data.accessToken as? String }.getOrNull()
        if (token.isNullOrBlank()) return false
        GuestSession.token = token
        return true
    }

    // --- Cookie SSO: one Google/Apple login shared by all venue subdomains ---

    /**
     * Silently exchanges the httpOnly session cookie for an access token.
     * Succeeds when the guest signed in via OAuth on ANY venue site before;
     * fills GuestSession and returns true. No cookie / expired -> false.
     */
    suspend fun trySilentSession(): Boolean {
        val response =
            runCatching {
                window.fetch(
                    AppConfig.getApiUrl("v1/auth/session"),
                    RequestInit(method = "POST", credentials = INCLUDE_CREDENTIALS),
                ).await()
            }.getOrNull() ?: return false
        if (!response.ok) return false
        val text = response.text().await()
        return runCatching {
            val data = JSON.parse<dynamic>(text).data
            val token = data.accessToken as? String
            val provider = data.provider as? String
            if (token.isNullOrBlank() || provider != "GOOGLE" && provider != "APPLE") {
                false
            } else {
                GuestSession.token = token
                GuestSession.oauthProvider = provider.lowercase()
                (data.user?.email as? String)?.let { GuestSession.email = it }
                true
            }
        }.getOrDefault(false)
    }

    /** Signs the guest out everywhere: revokes the cookie session, clears local state. */
    suspend fun sessionLogout() {
        runCatching {
            window.fetch(
                AppConfig.getApiUrl("v1/auth/session/logout"),
                RequestInit(method = "POST", credentials = INCLUDE_CREDENTIALS),
            ).await()
        }
        GuestSession.logout()
    }

    private var silentSessionMissed = false

    /**
     * Restores the Google/Apple session without any UI: an expired/absent access
     * token is silently re-minted from the httpOnly SSO cookie (access tokens
     * only live 15 minutes, the cookie much longer). Remembers a miss so that
     * anonymous browsing doesn't fire a session request per opened dish.
     */
    suspend fun restoreSessionQuietly(): Boolean {
        if (GuestSession.isOAuth) return true
        if (silentSessionMissed) return false
        val ok = runCatching { trySilentSession() }.getOrDefault(false)
        if (!ok) silentSessionMissed = true
        return ok
    }

    /**
     * Performs an authenticated /me request. On 401 (the 15-minute access token
     * expired) the SSO cookie is exchanged for a fresh token once and the request
     * retried — this is what keeps likes alive across page reloads. Null when
     * there is no usable session.
     */
    private suspend fun meFetch(
        path: String,
        method: String = "GET",
    ): org.w3c.fetch.Response? {
        suspend fun send(token: String) =
            window.fetch(
                AppConfig.getApiUrl(path),
                RequestInit(method = method, headers = json("Authorization" to "Bearer $token")),
            ).await()

        val token = GuestSession.token ?: return null
        val response = send(token)
        if (response.status.toInt() != 401) return response
        GuestSession.token = null
        // Recover the same identity: OAuth guests via the SSO cookie, anonymous
        // browsers by re-logging into their derived anon account.
        val recovered =
            runCatching { trySilentSession() }.getOrDefault(false) || tryAnonRelogin()
        if (!recovered) return null
        val fresh = GuestSession.token ?: return null
        val retried = send(fresh)
        if (retried.status.toInt() == 401) GuestSession.token = null
        return retried
    }

    /** Ids of items the signed-in guest already liked (empty when logged out or on error). */
    suspend fun likedItemIds(): Set<String> {
        val response = meFetch("v1/me/likes/items?size=200") ?: return emptySet()
        if (!response.ok) return emptySet()
        val text = response.text().await()
        return runCatching {
            val items = JSON.parse<dynamic>(text).data.items as Array<dynamic>
            items.mapNotNull { it.item?.id as? String }.toSet()
        }.getOrDefault(emptySet())
    }

    /** Rates a restaurant or item 1..5 with the guest token; false when unauthorized. */
    suspend fun rate(
        targetType: String,
        targetId: String,
        score: Int,
    ): Boolean {
        if (!ensureSession()) return false
        val token = GuestSession.token ?: return false
        val response =
            window.fetch(
                AppConfig.getApiUrl("v1/me/ratings"),
                RequestInit(
                    method = "PUT",
                    headers = json(
                        "Authorization" to "Bearer $token",
                        "Content-Type" to "application/json",
                    ),
                    body = JSON.stringify(
                        json("targetType" to targetType, "targetId" to targetId, "score" to score),
                    ),
                ),
            ).await()
        if (response.status.toInt() == 401) GuestSession.token = null
        return response.ok
    }

    // --- Loyalty (cashback) ---

    data class LoyaltyInfo(
        val enabled: Boolean,
        val ratePercent: Int,
        val balanceMinor: Int,
        val currency: String,
    )

    data class LoyaltyQr(
        val code: String,
        val expiresAt: String,
    )

    /**
     * Marks the session as not loyalty-eligible when the backend answers
     * 403 LOYALTY_AUTH_REQUIRED (e.g. a stale anon token wearing the OAuth flag);
     * the UI then falls back to the sign-in view.
     */
    private suspend fun handleLoyaltyForbidden(response: org.w3c.fetch.Response): Boolean {
        if (response.status.toInt() != 403) return false
        val text = runCatching { response.text().await() }.getOrNull()
        if (text != null && text.contains("LOYALTY_AUTH_REQUIRED")) {
            GuestSession.oauthProvider = null
        }
        return true
    }

    /** Cashback balance of this guest at a restaurant; null when logged out or on error. */
    suspend fun loyalty(slug: String): LoyaltyInfo? {
        val token = GuestSession.token ?: return null
        val response =
            window.fetch(
                AppConfig.getApiUrl("v1/me/loyalty/$slug"),
                RequestInit(headers = json("Authorization" to "Bearer $token")),
            ).await()
        if (response.status.toInt() == 401) {
            GuestSession.token = null
            return null
        }
        if (handleLoyaltyForbidden(response)) return null
        if (!response.ok) return null
        val text = response.text().await()
        return runCatching {
            val parsed = JSON.parse<dynamic>(text)
            val data = parsed.data ?: parsed
            LoyaltyInfo(
                enabled = data.enabled as? Boolean ?: false,
                ratePercent = (data.ratePercent as? Int) ?: 5,
                balanceMinor = (data.balanceMinor as? Int) ?: 0,
                currency = data.currency as? String ?: "EUR",
            )
        }.getOrNull()
    }

    /**
     * Issues a fresh single-use 6-digit loyalty code (TTL 10 min); null on failure.
     * Never provisions an account: loyalty requires an explicit OAuth session.
     */
    suspend fun loyaltyQr(slug: String): LoyaltyQr? {
        val token = GuestSession.token ?: return null
        val response =
            window.fetch(
                AppConfig.getApiUrl("v1/me/loyalty/$slug/qr"),
                RequestInit(
                    method = "POST",
                    headers = json("Authorization" to "Bearer $token"),
                ),
            ).await()
        if (response.status.toInt() == 401) {
            GuestSession.token = null
            return null
        }
        if (handleLoyaltyForbidden(response)) return null
        if (!response.ok) return null
        val text = response.text().await()
        return runCatching {
            val parsed = JSON.parse<dynamic>(text)
            val data = parsed.data ?: parsed
            val code = data.code as? String
            if (code.isNullOrBlank()) {
                null
            } else {
                LoyaltyQr(code = code, expiresAt = data.expiresAt as? String ?: "")
            }
        }.getOrNull()
    }

    /**
     * Likes/unlikes an item; false when failed. Counts server-side right away:
     * any guest can vote — a silently provisioned per-browser account keeps the
     * vote unique (the server dedups repeat likes from the same account).
     */
    suspend fun likeItem(
        itemId: String,
        like: Boolean,
    ): Boolean {
        if (!ensureSession()) return false
        val response = meFetch("v1/me/likes/items/$itemId", if (like) "PUT" else "DELETE")
        return response?.ok == true
    }
}
